// slices/donationSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import DonationService from "../services/DonationService";

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  } else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  } else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ==================== ASYNC THUNKS ====================

// -------- PUBLIC ROUTES --------

/**
 * Create donation with Stripe payment intent
 * POST /api/donations/create-payment
 */
export const createDonationWithPayment = createAsyncThunk(
  'donation/createDonationWithPayment',
  async (donationData, { rejectWithValue }) => {
    try {
      const response = await DonationService.createDonationWithPayment(donationData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Confirm successful Stripe payment
 * POST /api/donations/confirm-payment
 */
export const confirmPayment = createAsyncThunk(
  'donation/confirmPayment',
  async (paymentIntentId, { rejectWithValue }) => {
    try {
      console.log('🔐 Redux confirming payment:', paymentIntentId);

      const response = await DonationService.confirmPayment(paymentIntentId);

      console.log('✅ Redux payment confirmation response:', response);

      return response;
    } catch (error) {
      console.error('❌ Redux payment confirmation failed:', error);
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Create donation with Stripe Checkout Session
 * POST /api/donations/create-checkout
 */
export const createDonationWithCheckout = createAsyncThunk(
  'donation/createDonationWithCheckout',
  async (donationData, { rejectWithValue }) => {
    try {
      const response = await DonationService.createDonationWithCheckout(donationData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get donation by Stripe session ID
 * GET /api/donations/session/:sessionId
 */
export const getDonationBySessionId = createAsyncThunk(
  'donation/getDonationBySessionId',
  async (sessionId, { rejectWithValue }) => {
    try {
      const response = await DonationService.getDonationBySessionId(sessionId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get donation by Stripe payment ID
 * GET /api/donations/payment/:paymentId
 */
export const getDonationByPaymentId = createAsyncThunk(
  'donation/getDonationByPaymentId',
  async (paymentId, { rejectWithValue }) => {
    try {
      const response = await DonationService.getDonationByPaymentId(paymentId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get donation by donation number
 * GET /api/donations/number/:donationNumber
 */
export const getDonationByNumber = createAsyncThunk(
  'donation/getDonationByNumber',
  async (donationNumber, { rejectWithValue }) => {
    try {
      const response = await DonationService.getDonationByNumber(donationNumber);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get public donations (not hidden)
 * GET /api/donations/public
 */
export const getPublicDonations = createAsyncThunk(
  'donation/getPublicDonations',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await DonationService.getPublicDonations(params);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get featured donations
 * GET /api/donations/featured
 */
export const getFeaturedDonations = createAsyncThunk(
  'donation/getFeaturedDonations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await DonationService.getFeaturedDonations();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- ADMIN ROUTES --------

/**
 * Get all donations (admin only)
 * GET /api/donations
 */
export const getAllDonations = createAsyncThunk(
  'donation/getAllDonations',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await DonationService.getAllDonations(params);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get donation by ID (admin only)
 * GET /api/donations/:id
 */
export const getDonationById = createAsyncThunk(
  'donation/getDonationById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await DonationService.getDonationById(id);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get donations by donor email
 * GET /api/donations/donor/:email
 */
export const getDonationsByDonor = createAsyncThunk(
  'donation/getDonationsByDonor',
  async ({ email, params = {} }, { rejectWithValue }) => {
    try {
      const response = await DonationService.getDonationsByDonor(email, params);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Confirm donation (for manual payments)
 * PATCH /api/donations/:id/confirm
 */
export const confirmDonation = createAsyncThunk(
  'donation/confirmDonation',
  async (id, { rejectWithValue }) => {
    try {
      const response = await DonationService.confirmDonation(id);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Refund donation (Stripe)
 * POST /api/donations/:id/refund
 */
export const refundDonation = createAsyncThunk(
  'donation/refundDonation',
  async ({ id, reason = '' }, { rejectWithValue }) => {
    try {
      const response = await DonationService.refundDonation(id, reason);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Send thank you email to donor
 * POST /api/donations/:id/thank-you
 */
export const sendThankYou = createAsyncThunk(
  'donation/sendThankYou',
  async (id, { rejectWithValue }) => {
    try {
      const response = await DonationService.sendThankYou(id);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Add admin note to donation
 * POST /api/donations/:id/note
 */
export const addAdminNote = createAsyncThunk(
  'donation/addAdminNote',
  async ({ id, note }, { rejectWithValue }) => {
    try {
      const response = await DonationService.addAdminNote(id, note);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- SUPER ADMIN ROUTES --------

/**
 * Get donation analytics
 * GET /api/donations/analytics
 */
export const getDonationAnalytics = createAsyncThunk(
  'donation/getDonationAnalytics',
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await DonationService.getDonationAnalytics(startDate, endDate);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get donation trends
 * GET /api/donations/trends
 */
export const getDonationTrends = createAsyncThunk(
  'donation/getDonationTrends',
  async (days = 30, { rejectWithValue }) => {
    try {
      const response = await DonationService.getDonationTrends(days);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get today's donations
 * GET /api/donations/today
 */
export const getTodayDonations = createAsyncThunk(
  'donation/getTodayDonations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await DonationService.getTodayDonations();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get donation statistics
 * GET /api/donations/statistics
 */
export const getDonationStatistics = createAsyncThunk(
  'donation/getDonationStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await DonationService.getDonationStatistics();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  // Donation data
  donations: [],
  currentDonation: null,
  donorDonations: [],
  todayDonations: [],
  featuredDonations: [],
  publicDonations: [],
  
  // Payment
  paymentIntent: null,
  checkoutSession: null,
  clientSecret: null,
  
  // Statistics
  analytics: null,
  trends: [],
  statistics: null,
  
  // Pagination
  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  },
  
  // Filters
  filters: {},
  
  // Loading states
  loading: false,
  loadingDonation: false,
  loadingAnalytics: false,
  loadingTrends: false,
  loadingStatistics: false,
  submitting: false,
  updating: false,
  processing: false,

  // Stripe payment confirmation
  confirmingPayment: false,
  paymentConfirmationError: null,
  paymentConfirmationSuccess: false,
  
  // Error states
  error: null,
  submitError: null,
  updateError: null,
  processError: null,
  
  // Success states
  submitSuccess: false,
  updateSuccess: false,
  processSuccess: false,
  
  // UI states
  selectedDonationId: null,
  showPaymentModal: false,
  showRefundModal: false,
  isEditing: false,
};

const donationSlice = createSlice({
  name: 'donation',
  initialState,
  reducers: {
    // Reset states
    resetDonationState: (state) => {
      state.donations = [];
      state.currentDonation = null;
      state.donorDonations = [];
      state.todayDonations = [];
      state.featuredDonations = [];
      state.publicDonations = [];
      state.paymentIntent = null;
      state.checkoutSession = null;
      state.clientSecret = null;
      state.analytics = null;
      state.trends = [];
      state.statistics = null;
      state.pagination = {
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      };
      state.filters = {};
      state.error = null;
      state.submitError = null;
      state.updateError = null;
      state.processError = null;
      state.submitSuccess = false;
      state.updateSuccess = false;
      state.processSuccess = false;
      state.selectedDonationId = null;
      state.confirmingPayment = false;
      state.paymentConfirmationError = null;
      state.paymentConfirmationSuccess = false;
    },
    
    // Clear errors
    clearDonationErrors: (state) => {
      state.error = null;
      state.submitError = null;
      state.updateError = null;
      state.processError = null;
      state.paymentConfirmationError = null;
    },
    
    // Clear success states
    clearDonationSuccess: (state) => {
      state.submitSuccess = false;
      state.updateSuccess = false;
      state.processSuccess = false;
      state.paymentConfirmationSuccess = false;
    },
    
    // Set filters
    setDonationFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Clear filters
    clearDonationFilters: (state) => {
      state.filters = {};
    },
    
    // Set pagination
    setDonationPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Set selected donation
    setSelectedDonation: (state, action) => {
      state.selectedDonationId = action.payload;
    },
    
    // Toggle payment modal
    togglePaymentModal: (state, action) => {
      state.showPaymentModal = action.payload !== undefined ? action.payload : !state.showPaymentModal;
    },
    
    // Toggle refund modal
    toggleRefundModal: (state, action) => {
      state.showRefundModal = action.payload !== undefined ? action.payload : !state.showRefundModal;
    },
    
    // Set editing mode
    setEditingDonation: (state, action) => {
      state.isEditing = action.payload;
    },
    
    // Update donation locally (optimistic update)
    updateLocalDonation: (state, action) => {
      const { donationId, updateData } = action.payload;
      
      // Update in donations list
      const donationIndex = state.donations.findIndex(d => d._id === donationId);
      if (donationIndex !== -1) {
        state.donations[donationIndex] = { ...state.donations[donationIndex], ...updateData };
      }
      
      // Update current donation
      if (state.currentDonation && state.currentDonation._id === donationId) {
        state.currentDonation = { ...state.currentDonation, ...updateData };
      }
      
      // Update in public donations
      const publicIndex = state.publicDonations.findIndex(d => d._id === donationId);
      if (publicIndex !== -1) {
        state.publicDonations[publicIndex] = { ...state.publicDonations[publicIndex], ...updateData };
      }
      
      // Update in featured donations
      const featuredIndex = state.featuredDonations.findIndex(d => d._id === donationId);
      if (featuredIndex !== -1) {
        state.featuredDonations[featuredIndex] = { ...state.featuredDonations[featuredIndex], ...updateData };
      }
    },
    
    // Add local donation
    addLocalDonation: (state, action) => {
      state.donations.unshift(action.payload);
    },
    
    // Remove local donation
    removeLocalDonation: (state, action) => {
      const donationId = action.payload;
      state.donations = state.donations.filter(d => d._id !== donationId);
      state.publicDonations = state.publicDonations.filter(d => d._id !== donationId);
      state.featuredDonations = state.featuredDonations.filter(d => d._id !== donationId);
      if (state.currentDonation && state.currentDonation._id === donationId) {
        state.currentDonation = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== CREATE DONATION WITH PAYMENT ==========
      .addCase(createDonationWithPayment.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        state.submitSuccess = false;
      })
      .addCase(createDonationWithPayment.fulfilled, (state, action) => {
        state.submitting = false;
        state.submitSuccess = true;
        state.currentDonation = action.payload.data.donation;
        state.paymentIntent = action.payload.data.paymentIntentId;
        state.clientSecret = action.payload.data.clientSecret;
      })
      .addCase(createDonationWithPayment.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload || 'Failed to create donation';
        state.submitSuccess = false;
      })
      
    .addCase(confirmPayment.pending, (state) => {
  state.confirmingPayment = true;
  state.paymentConfirmationError = null;
  state.paymentConfirmationSuccess = false;
})
.addCase(confirmPayment.fulfilled, (state, action) => {
  state.confirmingPayment = false;
  state.paymentConfirmationSuccess = true;
  state.paymentConfirmationError = null;

  console.log('✅ Payment confirmed, updating state with:', action.payload);

  // Backend returns the completed donation
  const completedDonation = action.payload?.data;

  if (completedDonation) {
    state.currentDonation = completedDonation;

    // Update regular donations list
    const donationIndex = state.donations.findIndex(
      d => d._id === completedDonation._id
    );
    if (donationIndex !== -1) {
      state.donations[donationIndex] = completedDonation;
    }

    // Update public donations list
    const publicIndex = state.publicDonations.findIndex(
      d => d._id === completedDonation._id
    );
    if (publicIndex !== -1) {
      state.publicDonations[publicIndex] = completedDonation;
    }
    
    // Update featured donations if present
    const featuredIndex = state.featuredDonations.findIndex(
      d => d._id === completedDonation._id
    );
    if (featuredIndex !== -1) {
      state.featuredDonations[featuredIndex] = completedDonation;
    }
  }
})
.addCase(confirmPayment.rejected, (state, action) => {
  state.confirmingPayment = false;
  state.paymentConfirmationSuccess = false;
  state.paymentConfirmationError = action.payload || 'Failed to confirm donation payment';
})
      
      // ========== CREATE DONATION WITH CHECKOUT ==========
      .addCase(createDonationWithCheckout.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        state.submitSuccess = false;
      })
      .addCase(createDonationWithCheckout.fulfilled, (state, action) => {
        state.submitting = false;
        state.submitSuccess = true;
        state.currentDonation = action.payload.data.donation;
        state.checkoutSession = action.payload.data.sessionId;
      })
      .addCase(createDonationWithCheckout.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload || 'Failed to create checkout session';
        state.submitSuccess = false;
      })
      
      // ========== GET ALL DONATIONS ==========
      .addCase(getAllDonations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllDonations.fulfilled, (state, action) => {
        state.loading = false;
        state.donations = action.payload.data?.items || action.payload.data?.data || [];
        state.pagination = action.payload.data?.pagination || state.pagination;
      })
      .addCase(getAllDonations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get donations';
      })
      
      // ========== GET DONATION BY ID ==========
      .addCase(getDonationById.pending, (state) => {
        state.loadingDonation = true;
        state.error = null;
      })
      .addCase(getDonationById.fulfilled, (state, action) => {
        state.loadingDonation = false;
        state.currentDonation = action.payload.data;
      })
      .addCase(getDonationById.rejected, (state, action) => {
        state.loadingDonation = false;
        state.error = action.payload || 'Failed to get donation';
      })
      
      // ========== GET DONATIONS BY DONOR ==========
      .addCase(getDonationsByDonor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDonationsByDonor.fulfilled, (state, action) => {
        state.loading = false;
        state.donorDonations = action.payload.data?.items || action.payload.data?.data || [];
        state.pagination = action.payload.data?.pagination || state.pagination;
      })
      .addCase(getDonationsByDonor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get donor donations';
      })
      
      // ========== GET PUBLIC DONATIONS - FIXED ==========
      .addCase(getPublicDonations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPublicDonations.fulfilled, (state, action) => {
        state.loading = false;
        let donations = [];
        let pagination = state.pagination;
         
        if (action.payload) {
          if (action.payload.data) {
            if (Array.isArray(action.payload.data)) {
              donations = action.payload.data;
            } else if (action.payload.data.items && Array.isArray(action.payload.data.items)) {
              donations = action.payload.data.items;
            } else if (action.payload.data.data && Array.isArray(action.payload.data.data)) {
              donations = action.payload.data.data;
            }
          } else if (Array.isArray(action.payload)) {
            donations = action.payload;
          } else if (action.payload.items && Array.isArray(action.payload.items)) {
            donations = action.payload.items;
          }
          
          if (action.payload.pagination) {
            pagination = action.payload.pagination;
          } else if (action.payload.data?.pagination) {
            pagination = action.payload.data.pagination;
          }
        }
        
        state.publicDonations = donations;
        state.pagination = pagination;
      })
      .addCase(getPublicDonations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get public donations';
      })
      
      // ========== GET FEATURED DONATIONS ==========
      .addCase(getFeaturedDonations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFeaturedDonations.fulfilled, (state, action) => {
        state.loading = false;
        state.featuredDonations = action.payload.data || [];
      })
      .addCase(getFeaturedDonations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get featured donations';
      })
      
      // ========== CONFIRM DONATION ==========
      .addCase(confirmDonation.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(confirmDonation.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.currentDonation = action.payload.data;
        const index = state.donations.findIndex(d => d._id === action.payload.data._id);
        if (index !== -1) {
          state.donations[index] = action.payload.data;
        }
        const publicIndex = state.publicDonations.findIndex(d => d._id === action.payload.data._id);
        if (publicIndex !== -1) {
          state.publicDonations[publicIndex] = action.payload.data;
        }
      })
      .addCase(confirmDonation.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to confirm donation';
        state.updateSuccess = false;
      })
      
      // ========== REFUND DONATION ==========
      .addCase(refundDonation.pending, (state) => {
        state.processing = true;
        state.processError = null;
        state.processSuccess = false;
      })
      .addCase(refundDonation.fulfilled, (state, action) => {
        state.processing = false;
        state.processSuccess = true;
        state.currentDonation = action.payload.data.donation;
        const index = state.donations.findIndex(d => d._id === action.payload.data.donation._id);
        if (index !== -1) {
          state.donations[index] = action.payload.data.donation;
        }
        const publicIndex = state.publicDonations.findIndex(d => d._id === action.payload.data.donation._id);
        if (publicIndex !== -1) {
          state.publicDonations[publicIndex] = action.payload.data.donation;
        }
      })
      .addCase(refundDonation.rejected, (state, action) => {
        state.processing = false;
        state.processError = action.payload || 'Failed to refund donation';
        state.processSuccess = false;
      })
      
      // ========== SEND THANK YOU ==========
      .addCase(sendThankYou.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(sendThankYou.fulfilled, (state) => {
        state.updating = false;
        if (state.currentDonation) {
          state.currentDonation.thankYouSent = true;
          state.currentDonation.thankYouSentAt = new Date();
        }
      })
      .addCase(sendThankYou.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to send thank you';
      })
      
      // ========== ADD ADMIN NOTE ==========
      .addCase(addAdminNote.fulfilled, (state, action) => {
        state.currentDonation = action.payload.data;
      })
      
      // ========== GET DONATION ANALYTICS ==========
      .addCase(getDonationAnalytics.pending, (state) => {
        state.loadingAnalytics = true;
        state.error = null;
      })
      .addCase(getDonationAnalytics.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.analytics = action.payload.data;
      })
      .addCase(getDonationAnalytics.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.error = action.payload || 'Failed to get analytics';
      })
      
      // ========== GET DONATION TRENDS ==========
      .addCase(getDonationTrends.pending, (state) => {
        state.loadingTrends = true;
        state.error = null;
      })
      .addCase(getDonationTrends.fulfilled, (state, action) => {
        state.loadingTrends = false;
        state.trends = action.payload.data || [];
      })
      .addCase(getDonationTrends.rejected, (state, action) => {
        state.loadingTrends = false;
        state.error = action.payload || 'Failed to get trends';
      })
      
      // ========== GET TODAY'S DONATIONS ==========
      .addCase(getTodayDonations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTodayDonations.fulfilled, (state, action) => {
        state.loading = false;
        state.todayDonations = action.payload.data?.items || action.payload.data?.data || [];
      })
      .addCase(getTodayDonations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get today\'s donations';
      })
      
      // ========== GET DONATION STATISTICS ==========
      .addCase(getDonationStatistics.pending, (state) => {
        state.loadingStatistics = true;
        state.error = null;
      })
      .addCase(getDonationStatistics.fulfilled, (state, action) => {
        state.loadingStatistics = false;
        state.statistics = action.payload.data;
      })
      .addCase(getDonationStatistics.rejected, (state, action) => {
        state.loadingStatistics = false;
        state.error = action.payload || 'Failed to get donation statistics';
      });
  }
});

// ==================== SELECTORS ====================

// Base selector
const selectDonationState = (state) => state.donation;

// Donation data selectors
export const selectDonations = createSelector(
  selectDonationState,
  (donation) => donation.donations
);

export const selectCurrentDonation = createSelector(
  selectDonationState,
  (donation) => donation.currentDonation
);

export const selectDonorDonations = createSelector(
  selectDonationState,
  (donation) => donation.donorDonations
);

export const selectTodayDonations = createSelector(
  selectDonationState,
  (donation) => donation.todayDonations
);

export const selectFeaturedDonations = createSelector(
  selectDonationState,
  (donation) => donation.featuredDonations
);

export const selectPublicDonations = createSelector(
  selectDonationState,
  (donation) => donation.publicDonations
);

// Payment selectors
export const selectPaymentIntent = createSelector(
  selectDonationState,
  (donation) => donation.paymentIntent
);

export const selectCheckoutSession = createSelector(
  selectDonationState,
  (donation) => donation.checkoutSession
);

export const selectClientSecret = createSelector(
  selectDonationState,
  (donation) => donation.clientSecret
);

// Statistics selectors
export const selectDonationAnalytics = createSelector(
  selectDonationState,
  (donation) => donation.analytics
);

export const selectDonationTrends = createSelector(
  selectDonationState,
  (donation) => donation.trends
);

export const selectDonationStatistics = createSelector(
  selectDonationState,
  (donation) => donation.statistics
);

// Pagination selectors
export const selectDonationPagination = createSelector(
  selectDonationState,
  (donation) => donation.pagination
);

// Loading selectors
export const selectDonationsLoading = createSelector(
  selectDonationState,
  (donation) => donation.loading
);

export const selectDonationLoading = createSelector(
  selectDonationState,
  (donation) => donation.loadingDonation
);

export const selectAnalyticsLoading = createSelector(
  selectDonationState,
  (donation) => donation.loadingAnalytics
);

export const selectTrendsLoading = createSelector(
  selectDonationState,
  (donation) => donation.loadingTrends
);

export const selectStatisticsLoading = createSelector(
  selectDonationState,
  (donation) => donation.loadingStatistics
);

export const selectDonationSubmitting = createSelector(
  selectDonationState,
  (donation) => donation.submitting
);

export const selectDonationUpdating = createSelector(
  selectDonationState,
  (donation) => donation.updating
);

export const selectDonationProcessing = createSelector(
  selectDonationState,
  (donation) => donation.processing
);

// Error selectors
export const selectDonationError = createSelector(
  selectDonationState,
  (donation) => donation.error
);

export const selectSubmitDonationError = createSelector(
  selectDonationState,
  (donation) => donation.submitError
);

export const selectUpdateDonationError = createSelector(
  selectDonationState,
  (donation) => donation.updateError
);

export const selectProcessDonationError = createSelector(
  selectDonationState,
  (donation) => donation.processError
);

// Success selectors
export const selectSubmitDonationSuccess = createSelector(
  selectDonationState,
  (donation) => donation.submitSuccess
);

export const selectUpdateDonationSuccess = createSelector(
  selectDonationState,
  (donation) => donation.updateSuccess
);

export const selectProcessDonationSuccess = createSelector(
  selectDonationState,
  (donation) => donation.processSuccess
);

// UI selectors
export const selectSelectedDonationId = createSelector(
  selectDonationState,
  (donation) => donation.selectedDonationId
);

export const selectShowPaymentModal = createSelector(
  selectDonationState,
  (donation) => donation.showPaymentModal
);

export const selectShowRefundModal = createSelector(
  selectDonationState,
  (donation) => donation.showRefundModal
);

export const selectIsEditingDonation = createSelector(
  selectDonationState,
  (donation) => donation.isEditing
);

// Derived selectors
export const selectTotalDonationsCount = createSelector(
  selectDonationPagination,
  (pagination) => pagination.total
);

export const selectCompletedDonations = createSelector(
  selectDonations,
  (donations) => donations?.filter(d => d.status === 'completed') || []
);

export const selectPendingDonations = createSelector(
  selectDonations,
  (donations) => donations?.filter(d => d.status === 'pending') || []
);

export const selectTotalDonationAmount = createSelector(
  selectDonations,
  (donations) => donations?.reduce((sum, d) => sum + (d.amount || 0), 0) || 0
);

export const selectTodayTotalAmount = createSelector(
  selectTodayDonations,
  (donations) => donations?.reduce((sum, d) => sum + (d.amount || 0), 0) || 0
);

export const selectFeaturedDonationsCount = createSelector(
  selectFeaturedDonations,
  (donations) => donations?.length || 0
);

export const selectPublicDonationsCount = createSelector(
  selectPublicDonations,
  (donations) => donations?.length || 0
);

export const selectConfirmingPayment = createSelector(
  selectDonationState,
  donation => donation.confirmingPayment
);

export const selectPaymentConfirmationError = createSelector(
  selectDonationState,
  donation => donation.paymentConfirmationError
);

export const selectPaymentConfirmationSuccess = createSelector(
  selectDonationState,
  donation => donation.paymentConfirmationSuccess
);

// ==================== EXPORTS ====================

export const {
  resetDonationState,
  clearDonationErrors,
  clearDonationSuccess,
  setDonationFilters,
  clearDonationFilters,
  setDonationPagination,
  setSelectedDonation,
  togglePaymentModal,
  toggleRefundModal,
  setEditingDonation,
  updateLocalDonation,
  addLocalDonation,
  removeLocalDonation,
} = donationSlice.actions;

export default donationSlice.reducer;